import { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectClinicId,
  selectClinicProfile,
} from "../../../store/slices/authSlice";
import {
  selectIsModalOpen,
  selectSelectedAppointment,
  openModal,
  closeModal,
} from "../../../store/slices/calendarSlice";
import { useGetAppointmentsQuery } from "../../../store/services/calendarApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import { deriveClinicRole } from "../../../config/permissionsRegistry";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../../store/services/patientsApi";
import {
  useGetRoomsQuery,
  useGetRoomAppointmentsQuery,
} from "../../../store/services/roomsApi";
import { useToast } from "../../../contexts/ToastContext";
import AppointmentModal from "../../../components/calendar/AppointmentEntryModal";
import TherapistBar from "../../../components/calendar/TherapistBar";
import TherapistCalendarModal from "../../../components/calendar/TherapistCalendarModal";
import RoomBar from "../../../components/calendar/RoomBar";
import RoomCalendarModal from "../../../components/calendar/RoomCalendarModal";
import ClinicCalendarModal from "../../../components/calendar/ClinicCalendarModal";
import RoomsCalendarModal from "../../../components/calendar/RoomsCalendarModal";
import TeamCoverageGrid from "../../../components/calendar/coverage/TeamCoverageGrid";
import ContentLoader from "../../../components/shared/ContentLoader";
import {
  CalendarCheck,
  ArrowClockwise,
  UsersThree,
  Door,
  DoorOpen,
  CalendarBlank,
  Calendar,
} from "@phosphor-icons/react";
import { HelpButton } from "../../../components/shared";
import CustomClinikitActionButton from "../../../components/ui/CustomClinikitActionButton";
import "./AdminCalendarPage.css";

function AdminCalendarPage() {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const isModalOpen = useSelector(selectIsModalOpen);
  const selectedAppointment = useSelector(selectSelectedAppointment);
  const { showSuccess } = useToast();

  const [minimizedTherapists, setMinimizedTherapists] = useState({});
  const [minimizedRooms, setMinimizedRooms] = useState({});
  const [selectedTherapistForCalendar, setSelectedTherapistForCalendar] = useState(null);
  const [selectedRoomForCalendar, setSelectedRoomForCalendar] = useState(null);
  // Unified clinic-wide calendar (all therapists overlaid in one calendar)
  const [showClinicCalendar, setShowClinicCalendar] = useState(false);
  // Clinic calendar split by room (only offered when the clinic has 2+ active rooms)
  const [showRoomsCalendar, setShowRoomsCalendar] = useState(false);
  // Page tabs: "calendars" (therapist/room bars) | "coverage" (team availability grid)
  const [activeTab, setActiveTab] = useState("calendars");

  // Fetch all therapists in the clinic; rows only for assignable ones
  // (active + non-secretary — secretaries have no calendar).
  const { data: allClinicTherapists = [], isLoading: therapistsLoading } =
    useGetClinicTherapistsQuery({ clinicId }, { skip: !clinicId });
  const therapists = useMemo(
    // Secretaries have no calendar; inactive therapists stay visible as before.
    () => allClinicTherapists.filter((t) => deriveClinicRole(t) !== "secretary"),
    [allClinicTherapists]
  );

  // Fetch all rooms in the clinic
  const { data: rooms = [], isLoading: roomsLoading } =
    useGetRoomsQuery({ clinicId }, { skip: !clinicId });

  // Filter active rooms
  const activeRooms = useMemo(() => {
    return rooms.filter((room) => room.isActive !== false);
  }, [rooms]);

  // Calculate date range for today
  const todayRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return { start: today, end: endOfDay };
  }, []);

  // Calculate date range for upcoming appointments (next 30 days, excluding today)
  const upcomingRange = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const end = new Date(tomorrow);
    end.setDate(end.getDate() + 30);
    end.setHours(23, 59, 59, 999);

    return { start: tomorrow, end };
  }, []);

  // Fetch ALL appointments for today (no therapist filter - admin view)
  const {
    data: todayAppointments = [],
    isLoading: todayLoading,
    refetch: refetchToday,
    isFetching: todayFetching,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: null, // null = all therapists
      startDate: todayRange.start.toISOString(),
      endDate: todayRange.end.toISOString(),
    },
    { skip: !clinicId }
  );

  // Fetch ALL upcoming appointments (no therapist filter)
  const {
    data: upcomingAppointments = [],
    isLoading: upcomingLoading,
    refetch: refetchUpcoming,
    isFetching: upcomingFetching,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: null, // null = all therapists
      startDate: upcomingRange.start.toISOString(),
      endDate: upcomingRange.end.toISOString(),
    },
    { skip: !clinicId }
  );

  // Fetch patients for the modal dropdown (all clinic patients)
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId }
  );

  const isLoading = therapistsLoading || roomsLoading || todayLoading || upcomingLoading;
  const isFetching = todayFetching || upcomingFetching;

  const handleRefresh = () => {
    refetchToday();
    refetchUpcoming();
  };

  // Group appointments by room
  const appointmentsByRoom = useMemo(() => {
    const result = {};

    activeRooms.forEach((room) => {
      result[room.id] = {
        today: todayAppointments.filter((apt) => apt.roomId === room.id),
        upcoming: upcomingAppointments
          .filter((apt) => apt.roomId === room.id)
          .slice(0, 15),
      };
    });

    return result;
  }, [activeRooms, todayAppointments, upcomingAppointments]);

  // Group appointments by therapist
  const appointmentsByTherapist = useMemo(() => {
    const result = {};

    therapists.forEach((therapist) => {
      result[therapist.id] = {
        today: todayAppointments.filter(
          (apt) => apt.therapistId === therapist.id
        ),
        upcoming: upcomingAppointments
          .filter((apt) => apt.therapistId === therapist.id)
          .slice(0, 15), // Limit to 15 upcoming appointments per therapist
      };
    });

    return result;
  }, [therapists, todayAppointments, upcomingAppointments]);

  const handleAppointmentClick = (appointment) => {
    dispatch(openModal(appointment));
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const handleToggleMinimize = (therapistId) => {
    setMinimizedTherapists((prev) => ({
      ...prev,
      [therapistId]: !prev[therapistId],
    }));
  };

  const handleToggleMinimizeRoom = (roomId) => {
    setMinimizedRooms((prev) => ({
      ...prev,
      [roomId]: !prev[roomId],
    }));
  };

  // Open weekly calendar modal for a therapist
  const handleOpenCalendar = (therapist) => {
    setSelectedTherapistForCalendar(therapist);
  };

  // Open weekly calendar modal for a room
  const handleOpenRoomCalendar = (room) => {
    setSelectedRoomForCalendar(room);
  };

  // Close weekly calendar modal
  const handleCloseCalendarModal = () => {
    setSelectedTherapistForCalendar(null);
  };

  // Close room calendar modal
  const handleCloseRoomCalendarModal = () => {
    setSelectedRoomForCalendar(null);
  };

  // Handle appointment created from room calendar modal
  const handleAppointmentCreatedFromRoom = () => {
    showSuccess("הפגישה נוצרה בהצלחה");
    handleRefresh();
  };

  // Calculate total stats
  const totalStats = useMemo(() => {
    const todayTotal = todayAppointments.length;
    const upcomingTotal = upcomingAppointments.length;
    const todayDeclined = todayAppointments.filter(
      (apt) =>
        apt.whatsappInitialDeclined === true ||
        apt.whatsappReminderDeclined === true
    ).length;
    const roomsCount = activeRooms.length;

    return { todayTotal, upcomingTotal, todayDeclined, roomsCount };
  }, [todayAppointments, upcomingAppointments, activeRooms]);

  const formatDate = () => {
    return new Date().toLocaleDateString("he-IL", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="admin-calendar-page">
      {/* Header */}
      <div className="admin-calendar-header">
        <div className="header-right">
          <CalendarCheck size={28} weight="fill" className="header-icon" />
          <div className="header-titles">
            <h1>{activeRooms.length > 0 ? "יומנים וחדרים" : "יומנים ותורים"}</h1>
            <span className="header-date">{formatDate()}</span>
          </div>
        </div>

        <div className="header-left">
          <HelpButton featureKey="admin_calendar" />
          <div className="header-stats">
            <div className="header-stat">
              <UsersThree size={20} weight="fill" />
              <span>{therapists.length} מטפלים</span>
            </div>
            {totalStats.roomsCount > 0 && (
              <div className="header-stat rooms-stat">
                <Door size={20} weight="fill" />
                <span>{totalStats.roomsCount} חדרים</span>
              </div>
            )}
            <div className="header-stat">
              <CalendarCheck size={20} weight="fill" />
              <span>{totalStats.todayTotal} פגישות היום</span>
            </div>
          </div>

          <CustomClinikitActionButton
            icon={<CalendarBlank size={16} weight="fill" />}
            onClick={() => setShowClinicCalendar(true)}
            disabled={therapists.length === 0}
            title="יומן קליניקה מרוכז — תצוגה יומית/שבועית/חודשית של כל המטפלים יחד"
          >
            הצג יומן קליניקה
          </CustomClinikitActionButton>

          {activeRooms.length > 1 && (
            <CustomClinikitActionButton
              icon={<DoorOpen size={16} weight="fill" />}
              onClick={() => setShowRoomsCalendar(true)}
              title="יומן קליניקה מפוצל לפי חדרים — כל יום מחולק לעמודה לכל חדר"
            >
              הצג יומן קליניקה לפי חדרים
            </CustomClinikitActionButton>
          )}

          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={isFetching}
            title="רענן נתונים"
          >
            <ArrowClockwise
              size={20}
              weight="bold"
              className={isFetching ? "spinning" : ""}
            />
          </button>
        </div>
      </div>

      {/* Tabs — flat, flush, full-width (design 2026 §3) */}
      <div className="admin-calendar-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "calendars"}
          className={`admin-calendar-tab ${activeTab === "calendars" ? "active" : ""}`}
          onClick={() => setActiveTab("calendars")}
        >
          <Calendar size={15} weight={activeTab === "calendars" ? "fill" : "regular"} />
          <span>יומנים</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "coverage"}
          className={`admin-calendar-tab ${activeTab === "coverage" ? "active" : ""}`}
          onClick={() => setActiveTab("coverage")}
        >
          <UsersThree size={15} weight={activeTab === "coverage" ? "fill" : "regular"} />
          <span>זמינות הצוות</span>
        </button>
      </div>

      {/* Team availability grid */}
      {activeTab === "coverage" && (
        <div className="admin-calendar-coverage">
          <TeamCoverageGrid clinicId={clinicId} canEditAvailability showTherapistNames />
        </div>
      )}

      {/* Main Content - Horizontal Scrolling Therapist Bars */}
      {activeTab === "calendars" && (
      <div className="admin-calendar-content">
        {isLoading ? (
          <ContentLoader fill message="טוען יומנים..." />
        ) : therapists.length === 0 ? (
          <div className="admin-calendar-empty">
            <UsersThree size={48} weight="light" />
            <h3>אין מטפלים בקליניקה</h3>
            <p>הוסיפו מטפלים לקליניקה כדי לראות את היומנים שלהם כאן</p>
          </div>
        ) : (
          <div className="therapists-container">
            {/* Therapists */}
            {therapists.map((therapist) => (
              <TherapistBar
                key={therapist.id}
                therapist={therapist}
                todayAppointments={
                  appointmentsByTherapist[therapist.id]?.today || []
                }
                upcomingAppointments={
                  appointmentsByTherapist[therapist.id]?.upcoming || []
                }
                onAppointmentClick={handleAppointmentClick}
                onOpenCalendar={handleOpenCalendar}
                isMinimized={minimizedTherapists[therapist.id] || false}
                onToggleMinimize={handleToggleMinimize}
              />
            ))}

            {/* Rooms (inline with therapists) */}
            {activeRooms.map((room) => (
              <RoomBar
                key={room.id}
                room={room}
                todayAppointments={
                  appointmentsByRoom[room.id]?.today || []
                }
                upcomingAppointments={
                  appointmentsByRoom[room.id]?.upcoming || []
                }
                onAppointmentClick={handleAppointmentClick}
                onOpenCalendar={handleOpenRoomCalendar}
                isMinimized={minimizedRooms[room.id] || false}
                onToggleMinimize={handleToggleMinimizeRoom}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Appointment Modal */}
      {isModalOpen && (
        <AppointmentModal
          appointment={selectedAppointment}
          patients={patientsData?.patients || []}
          onClose={handleCloseModal}
        />
      )}

      {/* Therapist Calendar Modal - Weekly view for scheduling */}
      {selectedTherapistForCalendar && (
        <TherapistCalendarModal
          therapist={selectedTherapistForCalendar}
          onClose={handleCloseCalendarModal}
          onAppointmentCreated={handleAppointmentCreatedFromRoom}
        />
      )}

      {/* Room Calendar Modal - Weekly view for room schedule */}
      {selectedRoomForCalendar && (
        <RoomCalendarModal
          room={selectedRoomForCalendar}
          onClose={handleCloseRoomCalendarModal}
          onAppointmentCreated={handleAppointmentCreatedFromRoom}
        />
      )}

      {/* Clinic-wide unified calendar (all therapists in one calendar view) */}
      {showClinicCalendar && (
        <ClinicCalendarModal
          onClose={() => {
            setShowClinicCalendar(false);
            handleRefresh();
          }}
        />
      )}

      {/* Clinic calendar split by room (one column per room per day) */}
      {showRoomsCalendar && (
        <RoomsCalendarModal
          rooms={activeRooms}
          onClose={() => {
            setShowRoomsCalendar(false);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
}

export default AdminCalendarPage;
